//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import * as RadixTabs from '@radix-ui/react-tabs';
import React from 'react';
import styled, { css } from 'styled-components';

import { Mixins } from '@theme/mixins';
import { withTracking } from '@utils/withTracking';

/**
 * `Tabs` is a set of layered sections of content — known as tab panels — that
 * are displayed one at a time.
 *
 * Tabs.List contains the Tabs.Trigger components that are aligned along the edge
 * of the active content. Tabs.Content is the content associated with each trigger.
 *
 * Note: When using custom components as triggers it's crucial to apply the `forwardRef`
 * pattern to maintain functionality. Failure to do so may result in unresponsive or
 * incorrectly functioning triggers.
 *
 * For example, if we want to use our buttons from the atoms folder we can consider
 * using them like this:
 *   <Button buttonStyles={DefaultButtonStyles}>Label</Button>
 *
 * @example
 * <Tabs
 *  defaultValue="tab1"
 *  onValueChange={handleValueChange}
 *  orientation="horizontal"
 * >
 *   <Tabs.List>
 *     <Tabs.Trigger asChild disabled={isDisabled} value="tab1">
 *       <Button buttonStyles={DefaultButtonStyles}>Account</Button>
 *     </Tabs.Trigger>
 *     <Tabs.Trigger disabled={isDisabled} value="tab2">
 *       Password
 *     </Tabs.Trigger>
 *   </Tabs.List>
 *   <Tabs.Content value="tab1">
 *     Tab 1 Content
 *   </Tabs.Content>
 *   <Tabs.Content asChild value="tab2">
 *     <SomeComponent>
 *       Tab 2 Content
 *     </SomeComponent>
 *   </Tabs.Content>
 * </Tabs>
 */
export const Tabs = (props: RadixTabs.TabsProps) => <TabsRoot {...props} />;

const TabsRoot = styled(RadixTabs.Root)`
  ${Mixins.Sheet.Low}
  flex-direction: column;
`;

const TabsList = styled(RadixTabs.List)`
  display: flex;
`;

const TabsTrigger = withTracking(
  styled(RadixTabs.Trigger)(
    ({ theme }) => css`
      ${Mixins.Text.UiCopy}

      &[data-disabled] {
        ${Mixins.Text.UiCopyDisabled}
      }

      align-items: center;
      background-color: ${theme.colors.white};
      border: 0;
      box-shadow: none;
      flex: 1;
      justify-content: center;
      padding: ${theme.spacings[3]} ${theme.spacings[4]};

      &[data-state='active'] {
        box-shadow: 0 ${theme.spacings[1]} 0 0 inset
          ${theme.colors.aliases.primary};

        &:not(:first-of-type) {
          border-left: 1px solid ${theme.colors.gray2};
        }

        &:not(:last-of-type) {
          border-right: 1px solid ${theme.colors.gray2};
        }
      }

      &[data-state='inactive'] {
        border-bottom: 1px solid ${theme.colors.gray2};

        &:hover {
          &:not([disabled]) {
            background-color: ${theme.colors.gray00};
          }
        }
      }
    `,
  ),
);

Tabs.Content = RadixTabs.Content;
Tabs.List = TabsList;
Tabs.Trigger = TabsTrigger;
